import type { CookieOptions } from "@constants/cookies";
import { DEFAULT_COOKIE_OPTIONS } from "@constants/cookies";

type CookieSetter = (
  name: string,
  value: string,
  options?: CookieOptions,
) => void;

/**
 * Fallback lifetime when the policy cannot be read. Deliberately UNDER the 24h
 * the API mints (omg-api `cloudFrontSigningService.ts`, `VIDEO_COOKIE_TTL_MS`,
 * via `GET /media/cloudfront-cookies`), so the jar drops the triplet
 * while it is still valid rather than an hour after CloudFront starts refusing
 * it. Expiring a working cookie costs one round trip; keeping a dead one costs
 * a 403 that the browser then caches for 45 minutes (OMG-2533).
 */
export const CLOUDFRONT_COOKIE_FALLBACK_TTL_MS = 23 * 60 * 60 * 1000;

/** CloudFront's URL-safe base64 for signed cookies: `+/=` become `-~_`. */
const fromCloudFrontBase64 = (value: string) =>
  value.replace(/-/g, "+").replace(/~/g, "/").replace(/_/g, "=");

/**
 * When the signed policy stops being accepted, read out of the policy itself.
 *
 * The policy is the only party that knows: the API returns bare `name=value`
 * strings with no attributes, and the 24h it mints is not something this side
 * should restate. Returns undefined for anything it cannot parse, and the
 * caller falls back — a malformed policy is already broken, and guessing an
 * expiry for it would only decide how the breakage looks.
 */
export const cloudFrontPolicyExpiry = (policy: string): Date | undefined => {
  try {
    const decoded = JSON.parse(atob(fromCloudFrontBase64(policy))) as {
      Statement?: Array<{
        Condition?: { DateLessThan?: { "AWS:EpochTime"?: number } };
      }>;
    };
    const epochSeconds =
      decoded.Statement?.[0]?.Condition?.DateLessThan?.["AWS:EpochTime"];
    if (typeof epochSeconds !== "number" || !Number.isFinite(epochSeconds)) {
      return undefined;
    }
    return new Date(epochSeconds * 1000);
  } catch {
    return undefined;
  }
};

/**
 * Write the CloudFront signed-cookie triplet returned by the API
 * (`CloudFront-Policy` / `CloudFront-Signature` / `CloudFront-Key-Pair-Id`)
 * into the cookie jar, scoped via DEFAULT_COOKIE_OPTIONS (Domain=.omgyes.com)
 * so they are sent to videos.omgyes.com.
 *
 * Shared by the initial load (PageLayout) and the on-403 refresh
 * (useRefreshCloudFrontCookies) so the two paths cannot drift. Each entry is a
 * raw "name=value" string as returned by `GET /media/cloudfront-cookies`.
 *
 * THE TRIPLET EXPIRES WITH THE POLICY (#977 review item 1). These were session
 * cookies — DEFAULT_COOKIE_OPTIONS carries no `expires` and the API returns no
 * attributes — while the policy inside them is good for 24h. So at hour 25 the
 * jar entry was still there, carrying a policy CloudFront now rejects, and
 * `useCloudFrontCookiesReady` keys its whole gate on the cookie being ABSENT:
 * `waiting` was false, the gate released instantly, and the refetch that exists
 * for exactly this case never armed. What saved the member was the retry path
 * refreshing after a 403 — the thing the gate was added to avoid. Giving the
 * cookies the policy's own expiry is what makes "the cookie is present" mean
 * "the cookie is usable", which is the assumption every reader of that gate
 * makes.
 */
export const writeCloudFrontCookies = (
  cookies: string[],
  setCookie: CookieSetter,
) => {
  const parsed = cookies.map((cookie) => {
    const [name, ...valueParts] = cookie.split("=");
    return { name, value: valueParts.join("=") };
  });

  const policy = parsed.find(({ name }) => name === "CloudFront-Policy")?.value;
  const expires =
    (policy ? cloudFrontPolicyExpiry(policy) : undefined) ??
    new Date(Date.now() + CLOUDFRONT_COOKIE_FALLBACK_TTL_MS);

  // One expiry for all three: they are only ever useful together, and letting
  // the signature outlive the policy would leave a jar that looks half-signed.
  parsed.forEach(({ name, value }) => {
    setCookie(name, value, { ...DEFAULT_COOKIE_OPTIONS, expires });
  });
};
